import type {Observable} from 'protoc-gen-ts';
import {InferResponseType, MakeAllKeysRequired} from '../types';
import {GrpcConfig, createGrpcClientChannel} from './GrpcClientContext';
import {isUnimplementedError} from './code';
import {convertAllNullPropertiesToUndefined} from './convertAllNullPropertiesToUndefined';
import {GrpcRequestServiceMethod} from './useGenericGrpcRequest';
import {GenericTadaridaConfig} from './useGrpcRequest';

// This is essentially a port of `useGenericGrpcRequestFn` in packages/libraries/data-fetcher/src/grpc/useGenericGrpcRequest.ts
export function useStandaloneGrpcRequest<
  ServiceMethod extends GrpcRequestServiceMethod,
  ParamsType extends MakeAllKeysRequired<
    Omit<
      Parameters<Awaited<ReturnType<ServiceMethod>>>[1],
      keyof GenericTadaridaConfig
    >
  >,
  ResponseType extends InferResponseType<
    ReturnType<Awaited<ReturnType<ServiceMethod>>>
  >,
>(
  requestConfig: {method: ServiceMethod; params: ParamsType},
  {
    hostConfig,
    grpcConfig,
    legacyGrpcConfig,
    legacy,
    mockData,
    resolveMockData = 'not-implemented',
  }: {
    hostConfig: {hostname: string; legacyHostname: string};
    grpcConfig: GrpcConfig;
    legacyGrpcConfig?: {
      shopId: number;
      childShopId: number;
      device: string;
    };
    legacy?: boolean;

    /**
     * If `not-implemented`, only returns the data in case a NotImplemented response is returned from the backend
     * @default 'not-implemented'
     */
    resolveMockData?: 'always' | 'not-implemented';
    mockData?: (params?: Record<string, unknown>) => Promise<ResponseType>;
  },
) {
  const settings = {
    debug: false,
    ...hostConfig,
  };

  const {method, params} = requestConfig;

  return async (): Promise<ResponseType> => {
    const [channel, SyncServiceMethod] = await Promise.all([
      createGrpcClientChannel({legacy}, settings),
      await method(),
    ]);

    let fetcher: Promise<ResponseType> | Observable<ResponseType>;

    if (legacy) {
      fetcher = SyncServiceMethod(channel, {
        ...legacyGrpcConfig,
        ...convertAllNullPropertiesToUndefined<ParamsType>(params),
      });
    } else {
      fetcher = SyncServiceMethod(channel, {
        config: grpcConfig,
        ...convertAllNullPropertiesToUndefined<ParamsType>(params),
      });
    }

    if (mockData) {
      return new Promise<ResponseType>(async (resolve, reject) => {
        if (resolveMockData === 'always') {
          return mockData(params).then(resolve);
        } else {
          (fetcher as Promise<ResponseType>)
            .then(response => resolve(response))
            .catch(async error => {
              if (
                isUnimplementedError(error) &&
                resolveMockData === 'not-implemented'
              ) {
                return mockData(params).then(resolve);
              }
              return reject(error);
            });
        }
      });
    }

    return fetcher as Promise<ResponseType>;
  };
}
